import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Table, Spinner, Button, Form, InputGroup, Modal,
    Row, Col, Badge, Alert as BsAlert
} from "react-bootstrap";
import SafetyCorsi81Service from "../../services/safety/SafetyCorsi81Service";

const CATEGORIE_LABEL = {
    LAVORATORE: "Lavoratori",
    PREPOSTO: "Preposto",
    DIRIGENTE: "Dirigente",
    DATORE: "Datore di lavoro",
    RSPP_DDL: "RSPP Datore di lavoro",
    RSPP: "RSPP / ASPP",
    ASPP: "ASPP",
    RLS: "RLS",
    ANTINCENDIO: "Antincendio",
    PRIMO_SOCCORSO: "Primo soccorso",
    CSP_CSE: "Coordinatori cantieri",
    ATTREZZATURE: "Attrezzature",
    SPAZI_CONFINATI: "Spazi confinati",
    ALTRO: "Altro",
};

async function unwrap(resp) {
    return resp && typeof resp.json === "function" ? await resp.json() : resp;
}

export default function SafetyMappatura81Page() {
    const [loading, setLoading]   = useState(true);
    const [corsi, setCorsi]       = useState([]);
    const [tipi, setTipi]         = useState([]);
    const [q, setQ]               = useState("");
    const [stato, setStato]       = useState("tutti"); // tutti | mappati | nonmappati
    const [errore, setErrore]     = useState(null);

    const [modalShow, setModalShow] = useState(false);
    const [editing, setEditing]     = useState(null);   // corso in mappatura
    const [selected, setSelected]   = useState({});     // { id_tipo: true }
    const [tipiQ, setTipiQ]         = useState("");
    const [saving, setSaving]       = useState(false);
    const [formError, setFormError] = useState(null);

    const loadTipi = useCallback(async () => {
        try {
            const data = await unwrap(await SafetyCorsi81Service.getTipi());
            setTipi(Array.isArray(data) ? data : []);
        } catch (err) {
            setErrore(err?.message || "Errore nel caricamento del catalogo 81.");
        }
    }, []);

    const loadCorsi = useCallback(async () => {
        setLoading(true);
        setErrore(null);
        try {
            const filtri = {};
            if (q) filtri.q = q;
            if (stato === "mappati") filtri.solo_mappati = 1;
            if (stato === "nonmappati") filtri.solo_non_mappati = 1;
            const data = await unwrap(await SafetyCorsi81Service.getCorsi(filtri));
            setCorsi(Array.isArray(data) ? data : []);
        } catch (err) {
            setErrore(err?.message || "Errore nel caricamento dei corsi.");
        } finally {
            setLoading(false);
        }
    }, [q, stato]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadCorsi(); }, [loadCorsi]);

    const openMap = (corso) => {
        setEditing(corso);
        const sel = {};
        (corso.tipi || []).forEach(t => { sel[t.id_tipo] = true; });
        setSelected(sel);
        setTipiQ("");
        setFormError(null);
        setModalShow(true);
    };

    const closeModal = () => { if (!saving) setModalShow(false); };

    const toggleTipo = (id_tipo) => {
        setSelected(prev => {
            const next = { ...prev };
            if (next[id_tipo]) delete next[id_tipo];
            else next[id_tipo] = true;
            return next;
        });
    };

    const handleSave = async () => {
        if (!editing) return;
        setSaving(true);
        setFormError(null);
        try {
            const ids = Object.keys(selected).filter(k => selected[k]).map(Number);
            await SafetyCorsi81Service.saveMapping(editing.id_corso, ids);
            setModalShow(false);
            await loadCorsi();
        } catch (err) {
            setFormError(err?.message || "Errore durante il salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    // Catalogo tipi raggruppato per categoria (per il modale)
    const tipiByCategoria = useMemo(() => {
        const s = tipiQ.trim().toLowerCase();
        const filtered = tipi.filter(t =>
            !s || (t.denominazione || "").toLowerCase().includes(s) || (t.codice || "").toLowerCase().includes(s)
        );
        const groups = {};
        filtered.forEach(t => {
            const cat = t.categoria || "ALTRO";
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(t);
        });
        return groups;
    }, [tipi, tipiQ]);

    const selectedCount = Object.keys(selected).filter(k => selected[k]).length;

    const periodLabel = (mesi) => {
        if (!mesi) return null;
        if (mesi % 12 === 0) return `agg. ${mesi / 12} anni`;
        return `agg. ${mesi} mesi`;
    };

    return (
        <div className="p-2 p-md-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">
                        <i className="bi bi-diagram-3 me-2 text-primary" />
                        Mappatura corsi 81/08
                    </h4>
                    <div className="text-muted small">
                        Collega i corsi della piattaforma alle tipologie normative 81/08 (catalogo globale High Educational).
                    </div>
                </div>
                <Button variant="outline-secondary" onClick={loadCorsi}>
                    <i className="bi bi-arrow-clockwise me-1" /> Aggiorna
                </Button>
            </div>

            {errore && <BsAlert variant="danger" onClose={() => setErrore(null)} dismissible>{errore}</BsAlert>}

            <BsAlert variant="info" className="py-2">
                <i className="bi bi-info-circle me-1" />
                La mappatura serve a riconoscere "che tipo 81 e" ogni corso erogato: da qui si potranno
                classificare gli attestati e calcolare in automatico le scadenze. Un corso puo' essere
                associato a piu' tipi (es. Generale + Specifica).
            </BsAlert>

            <Row className="g-2 align-items-end mb-3">
                <Col xs={12} sm={6} md={5}>
                    <Form.Label className="small text-muted mb-1">Cerca corso</Form.Label>
                    <InputGroup>
                        <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                        <Form.Control
                            placeholder="Denominazione o codice corso"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") loadCorsi(); }}
                        />
                    </InputGroup>
                </Col>
                <Col xs={12} sm={6} md={4}>
                    <Form.Label className="small text-muted mb-1">Stato mappatura</Form.Label>
                    <Form.Select value={stato} onChange={e => setStato(e.target.value)}>
                        <option value="tutti">Tutti i corsi</option>
                        <option value="mappati">Solo mappati</option>
                        <option value="nonmappati">Solo da mappare</option>
                    </Form.Select>
                </Col>
                <Col xs="auto">
                    <Button variant="outline-secondary" onClick={loadCorsi}>
                        <i className="bi bi-funnel me-1" /> Filtra
                    </Button>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-5"><Spinner /></div>
            ) : corsi.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <i className="bi bi-journal-x fs-1 d-block mb-2 opacity-50" />
                    Nessun corso trovato con i criteri indicati.
                </div>
            ) : (
                <div className="table-responsive bg-white rounded shadow-sm">
                    <Table hover className="mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Corso</th>
                                <th>Tipi 81 associati</th>
                                <th className="text-end">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {corsi.map(c => (
                                <tr key={c.id_corso}>
                                    <td>
                                        <div className="fw-semibold">{c.denominazione}</div>
                                        <div className="small text-muted">
                                            {c.codice ? `cod. ${c.codice}` : ""}{c.versione ? ` · v${c.versione}` : ""}
                                        </div>
                                    </td>
                                    <td>
                                        {(c.tipi && c.tipi.length > 0) ? (
                                            <div className="d-flex flex-wrap gap-1">
                                                {c.tipi.map(t => (
                                                    <Badge key={t.id_tipo} bg="primary" className="fw-normal">
                                                        {t.denominazione}
                                                        {periodLabel(t.periodicita_aggiornamento_mesi)
                                                            ? <span className="opacity-75"> ({periodLabel(t.periodicita_aggiornamento_mesi)})</span>
                                                            : null}
                                                    </Badge>
                                                ))}
                                            </div>
                                        ) : (
                                            <Badge bg="light" text="dark">Da mappare</Badge>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        <Button size="sm" variant="outline-primary" onClick={() => openMap(c)}>
                                            <i className="bi bi-diagram-3 me-1" /> Mappa
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={modalShow} onHide={closeModal} size="lg" backdrop="static" scrollable>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>
                        Mappatura 81 — <span className="text-primary">{editing?.denominazione}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && <BsAlert variant="danger">{formError}</BsAlert>}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="small text-muted">
                            Seleziona una o piu' tipologie normative che questo corso soddisfa.
                        </div>
                        <Badge bg={selectedCount > 0 ? "primary" : "secondary"}>{selectedCount} selezionati</Badge>
                    </div>
                    <InputGroup className="mb-3" size="sm">
                        <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                        <Form.Control
                            placeholder="Filtra tipologie (es. preposto, antincendio, specifica)"
                            value={tipiQ}
                            onChange={e => setTipiQ(e.target.value)}
                        />
                    </InputGroup>

                    {Object.keys(tipiByCategoria).length === 0 ? (
                        <div className="text-muted small">Nessuna tipologia corrisponde al filtro.</div>
                    ) : (
                        Object.keys(tipiByCategoria).map(cat => (
                            <div key={cat} className="mb-3">
                                <div className="fw-semibold text-uppercase small text-muted mb-1">
                                    {CATEGORIE_LABEL[cat] || cat}
                                </div>
                                <Row className="g-1">
                                    {tipiByCategoria[cat].map(t => (
                                        <Col xs={12} md={6} key={t.id_tipo}>
                                            <Form.Check
                                                type="checkbox"
                                                id={`tipo-${t.id_tipo}`}
                                                checked={!!selected[t.id_tipo]}
                                                onChange={() => toggleTipo(t.id_tipo)}
                                                label={
                                                    <span>
                                                        {t.denominazione}
                                                        {periodLabel(t.periodicita_aggiornamento_mesi)
                                                            ? <span className="text-muted small"> · {periodLabel(t.periodicita_aggiornamento_mesi)}</span>
                                                            : <span className="text-muted small"> · una tantum</span>}
                                                    </span>
                                                }
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        ))
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal} disabled={saving}>Annulla</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-check2 me-1" />}
                        Salva mappatura
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
